<script setup>
// ============================================================================
// BonBoZ: TAB "GIAO DIỆN" — màu nhận diện của từng trang
// (docs/bonboz/pancake-settings-ui-reference.md §4 tab 4).
//
// 10+ page dùng chung bộ nhận diện BonBoZ, avatar không phân biệt được trang.
// Màu là kênh nhận diện duy nhất còn lại — và nó chỉ có giá trị ở NƠI LÀM VIỆC,
// nên `InboxName.vue` đọc lại đúng bản đồ màu này (helper/pageConfig.js).
//
// Lưu vào cấu hình theo trang, khoá `color`. PATCH là gộp nông nên khoá
// `labels` / `canned_response_ids` trên cùng trang không bị xoá theo.
// Chỉ admin: route khai `permissions: ['administrator']`, API trả 403.
//
// Công tắc "Hiển thị tài khoản được phân công chính" (dòng còn lại của tab bên
// Pancake) ngoài phạm vi MVP1.
// ============================================================================
import { onMounted, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import PageConfigsAPI, { loiApi } from 'dashboard/api/pageConfigs';
import {
  loadPageConfigs,
  setPageConfig,
  pageColorOf,
  pageConfigs,
} from 'dashboard/helper/pageConfig';

import SettingsLayout from '../SettingsLayout.vue';
import ConfigRow from '../components/ConfigRow.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ColorPicker from 'dashboard/components-next/colorpicker/ColorPicker.vue';

const { t } = useI18n();
const store = useStore();
const inboxes = useMapGetter('inboxes/getInboxes');

const isLoading = ref(true);
const error = ref('');

const colorOf = pageColorOf;

onMounted(async () => {
  if (!inboxes.value.length) store.dispatch('inboxes/get');
  // `force`: bản đồ có thể đã được nạp từ lúc mở màn Hội thoại.
  await loadPageConfigs(true);
  isLoading.value = false;
});

// ponytail: bộ chọn màu bắn sự kiện theo từng bước kéo chuột / từng ký tự gõ
// vào ô HEX — chờ 600ms rồi mới PUT, một hằng số thay cho một hàng đợi.
const timers = {};
const save = async (inboxId, hex) => {
  try {
    // Chỉ gửi khoá `color` — server gộp, cấu hình khác của trang giữ nguyên,
    // rồi trả về TOÀN BỘ cấu hình sau khi gộp: dùng thẳng, khỏi GET lại.
    const { data } = await PageConfigsAPI.update(inboxId, { color: hex });
    setPageConfig(inboxId, data);
    error.value = '';
  } catch (e) {
    error.value = loiApi(e);
  }
};

const pickColor = (inboxId, hex) => {
  setPageConfig(inboxId, { color: hex || null });
  clearTimeout(timers[inboxId]);
  timers[inboxId] = setTimeout(() => save(inboxId, hex || null), 600);
};

onBeforeUnmount(() => Object.values(timers).forEach(clearTimeout));

// BonBoZ: câu lời mời kết bạn Zalo mặc định (chỉ hộp thư Channel::Api).
// Lưu khi rời ô; rỗng gửi null = xoá khoá, panel dùng câu dự phòng.
const loiMoiOf = inboxId =>
  pageConfigs.value?.[String(inboxId)]?.zalo_loi_moi_ket_ban || '';
const luuLoiMoi = async (inboxId, text) => {
  const value = text.trim() || null;
  if (value === (loiMoiOf(inboxId) || null)) return;
  try {
    const { data } = await PageConfigsAPI.update(inboxId, {
      zalo_loi_moi_ket_ban: value,
    });
    setPageConfig(inboxId, data);
    error.value = '';
  } catch (e) {
    error.value = loiApi(e);
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('BONBOZ.PAGE_APPEARANCE.LOADING')"
    :no-records-found="!inboxes.length"
    :no-records-message="$t('BONBOZ.PAGE_APPEARANCE.EMPTY')"
  >
    <template #body>
      <!-- BonBoZ: card trắng radius 8 của Pancake (reference §1). -->
      <div class="p-6 bg-[rgb(var(--pk-the-nen))] rounded-lg">
        <ConfigRow
          icon="i-lucide-paintbrush"
          :title="t('BONBOZ.PAGE_APPEARANCE.SHOW_PAGES')"
          :description="t('BONBOZ.PAGE_APPEARANCE.SHOW_PAGES_HINT')"
        />

        <p v-if="error" class="mt-4 mb-0 text-sm text-n-ruby-11">{{ error }}</p>

        <!-- BonBoZ: hàng page — min-height 84px, nền #F2F4F7 radius 6,
             hover #EAECF0, tên 14/22 w500 ellipsis 300px, dấu • phân cách,
             ID page màu mờ, ô màu viên thuốc 68×20 (reference §4 tab 4).
             Dải màu dọc mép trái: đọc từ ảnh chụp 20/08/2026 — CSS không có,
             đây là chỗ Pancake xem trước màu ngay trên hàng. -->
        <div
          v-for="inbox in inboxes"
          :key="inbox.id"
          class="group flex flex-wrap items-center justify-between gap-4 min-h-[84px] mt-4 px-4 border-l-4 rounded-md bg-[rgb(var(--pk-hover))] hover:bg-[rgb(var(--pk-hover-dam))]"
          :style="{ borderLeftColor: colorOf(inbox.id) || 'transparent' }"
        >
          <div class="flex items-center min-w-0 gap-3">
            <Avatar
              :src="inbox.avatar_url"
              :name="inbox.name || '?'"
              :size="32"
              rounded-full
            />
            <div class="flex items-center min-w-0 text-sm leading-[22px]">
              <span
                class="truncate max-w-[300px] font-medium text-[rgb(var(--pk-chu-chinh))]"
              >
                {{ inbox.name }}
              </span>
              <template v-if="inbox.page_id">
                <span
                  class="mx-1.5 text-[rgb(var(--pk-chu-mo))] before:content-['•']"
                  aria-hidden="true"
                />
                <span class="text-[rgb(var(--pk-chu-phu))]">
                  {{ inbox.page_id }}
                </span>
              </template>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <span
              class="w-[68px] h-5 rounded-[20px] shrink-0"
              :style="{
                backgroundColor: colorOf(inbox.id) || 'rgb(var(--pk-vien-tat))',
              }"
            />
            <!-- Điều khiển ẩn tới khi hover, đúng thói quen Pancake
                 (reference §5.4).

                 BonBoZ: chỉ từ 768px trở lên. Màn cảm ứng không rê chuột được
                 nên dưới ngưỡng đó nút chọn màu và xoá màu KHÔNG BAO GIỜ hiện —
                 trang này trở thành chỉ để xem. -->
            <div
              class="flex gap-2 items-center md:invisible md:group-hover:visible"
            >
              <!-- Bảng màu bung ra là `absolute` neo mép TRÁI nút, mà nút nằm
                   sát mép phải khối 936px — nó tràn ra ngoài và làm cả trang
                   Cài đặt mọc thanh cuộn ngang. Neo mép PHẢI để bung vào trong. -->
              <ColorPicker
                class="[&_.vc-chrome]:!right-0"
                :model-value="colorOf(inbox.id)"
                @update:model-value="hex => pickColor(inbox.id, hex)"
              />
              <!-- Nút này LUÔN chiếm chỗ, chỉ ẩn đi khi trang chưa có màu.
                   Dùng `v-if` thì hàng chưa có màu thiếu mất bề ngang của nút,
                   bộ chọn màu bị đẩy sang phải lệch hẳn so với các hàng khác —
                   và chọn xong màu là nó nhảy ngược sang trái. -->
              <Button
                :label="t('BONBOZ.PAGE_APPEARANCE.CLEAR')"
                size="sm"
                slate
                faded
                :disabled="!colorOf(inbox.id)"
                :class="{ invisible: !colorOf(inbox.id) }"
                @click="pickColor(inbox.id, '')"
              />
            </div>
          </div>

          <label
            v-if="inbox.channel_type === 'Channel::Api'"
            class="basis-full pb-4 text-sm text-[rgb(var(--pk-chu-phu))]"
          >
            {{ t('BONBOZ.KET_BAN.CAU_HINH_NHAN') }}
            <textarea
              :key="loiMoiOf(inbox.id)"
              :value="loiMoiOf(inbox.id)"
              rows="2"
              :placeholder="
                t('BONBOZ.KET_BAN.CAU_HINH_GOI_Y', { ten: inbox.name })
              "
              class="block w-full mt-1 !mb-0 text-sm resize-none"
              @blur="luuLoiMoi(inbox.id, $event.target.value)"
            />
          </label>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
